import type { RestaurantResponseData } from '@/lib/types.ts';
import type { WidgetState } from './types.ts';

export const COACHELLA_EVENT_RESTAURANT_IDS = [794, 758, 731] as const;

const INCLUDES_GUEST_LABEL_RESTAURANT_IDS = [1233] as const;

/**
 * Returns the guest count label for a ticket or add-on.
 * Restaurant 1233 uses "Includes X guests" instead of the default "Up to X guests".
 * @param guests - Number of guests included with the ticket
 * @param restaurantId - Current restaurant ID
 * @returns Formatted guest label string
 * @example getGuestLabel(4, 1233) // "Includes 4 guests"
 * @example getGuestLabel(4, 100)  // "Up to 4 guests"
 */
export const getGuestLabel = (guests: number, restaurantId: number | null): string => {
  if (guests <= 1) return '1 guest';
  const useIncludes =
    typeof restaurantId === 'number' &&
    INCLUDES_GUEST_LABEL_RESTAURANT_IDS.includes(
      restaurantId as (typeof INCLUDES_GUEST_LABEL_RESTAURANT_IDS)[number]
    );
  return useIncludes ? `Includes ${guests} guests` : `Up to ${guests} guests`;
};

const LEGACY_PRICE_LABEL_RESTAURANT_IDS = [794] as const;

/**
 * Whether a restaurant uses the legacy "$x / person" price label
 * instead of the default "$x" over a "min spend pp" second line.
 * @param restaurantId - Current restaurant ID
 * @returns true if the restaurant should show the legacy format
 * @example isLegacyPriceLabel(794) // true
 * @example isLegacyPriceLabel(100) // false
 */
export const isLegacyPriceLabel = (restaurantId: number | null): boolean =>
  typeof restaurantId === 'number' &&
  LEGACY_PRICE_LABEL_RESTAURANT_IDS.includes(
    restaurantId as (typeof LEGACY_PRICE_LABEL_RESTAURANT_IDS)[number]
  );

/** All non-booking route segments that use the event shell */
export const WIDGET_ROUTE_SEGMENTS = [
  // New unified routes
  'details',
  'verify',
  'payment',
  'checkout',
  'confirmed',
  // Legacy event routes (redirected but still need shell detection)
  'eventinfo',
  'eventphone',
  'eventpayment',
  'eventconfirm',
  'eventconfirmation',
  // Legacy classic routes (redirected)
  'info',
  'phone',
  'confirm',
  'confirmation',
] as const;

export const isEventRestaurant = (
  restaurant: RestaurantResponseData | null | undefined,
  restaurantId: number | null | undefined
): boolean =>
  restaurant?.type === 'event' ||
  (typeof restaurantId === 'number' &&
    COACHELLA_EVENT_RESTAURANT_IDS.includes(
      restaurantId as (typeof COACHELLA_EVENT_RESTAURANT_IDS)[number]
    ));

/**
 * Step map: 0 = no indicator, 1-4 = step number.
 *
 * The widget flow always has 4 steps for instant booking:
 *   1=booking, 2=details/verify, 3=payment, 4=checkout
 *
 * Custom request flow hides steps entirely since it has its own
 * internal step indicators.
 */
const STEP_MAP: Record<string, number> = {
  booking: 0,
  details: 2,
  verify: 2,
  payment: 3,
  checkout: 4,
  confirmed: 0,
  // Legacy routes
  info: 2,
  eventinfo: 2,
  phone: 2,
  eventphone: 2,
  eventpayment: 3,
  confirm: 4,
  eventconfirm: 4,
  confirmation: 0,
  eventconfirmation: 0,
};

export const getEventStep = (
  pathname: string,
  _isLoggedIn: boolean,
  bookingType?: string
): { step: number; totalSteps: number } => {
  // Custom request flow hides all step indicators
  if (bookingType === 'custom_request') {
    return { step: 0, totalSteps: 0 };
  }

  const segment = pathname.split('/').pop() ?? '';
  const step = STEP_MAP[segment] ?? 0;

  return { step, totalSteps: step > 0 ? 4 : 0 };
};

export const getActiveEventId = (widget: WidgetState): number | null => {
  const eventId = widget.event?.id ?? widget.eventInfo?.id ?? null;
  return typeof eventId === 'number' && eventId > 0 ? eventId : null;
};

export const getSelectedTicketId = (widget: WidgetState): number | null =>
  widget.eventInfo?.ticketItemId ?? widget.option?.seating_id ?? null;

export const hasEventSelection = (widget: WidgetState): boolean =>
  getActiveEventId(widget) !== null && getSelectedTicketId(widget) !== null;

/** Check if the user has made any booking selection (event or restaurant) */
export const hasBookingSelection = (widget: WidgetState): boolean => !!widget.bookingType;
